/**
 * Cele cinci grafice ale tabloului "My Performance", desenate ca SVG.
 *
 * In portal sunt Chart.js 2 (`public/js/dashboards/easeedesk/my_performance.js`,
 * cinci `new Chart(...)`): o placinta "Active / Closed", un inel de SLA cu
 * scrisul in mijloc, o coloana "Top Ticket Types", o linie "Tickets / hours" cu
 * trei serii, si inca o placinta pentru echipa.
 *
 * DE CE NU CHART.JS: v4 nu are nicio biblioteca de grafice, si celelalte
 * tablouri mutate deja (`SimpleLineChart`, `NetworkCharts`, `AreaChart`) si-au
 * desenat singure formele. Un al saselea fel de a desena un cerc nu ajuta pe
 * nimeni; aceleasi culori si aceleasi cifre, insa, da.
 *
 * AMCHARTS NU SE ADUCE INAPOI. `loadPlugins` din controllerul vechi cerea patru
 * pachete amcharts, dar in tot scriptul nu e niciun `AmCharts.makeChart` -
 * randuri moarte. amCharts a iesit din portal pe 6 septembrie 2026.
 *
 * O DIFERENTA, SPUSA PE FATA: Chart.js arata valoarea intr-un balon la trecerea
 * cu mausul. Aici valoarea sta scrisa in legenda si pe bare, ca sa se vada si
 * fara maus (si ca sa se poata citi de o proba).
 */

const FONT = "'Helvetica Neue', Helvetica, Arial, sans-serif"
const GRID = '#e5e5e5'
const TEXT = '#666'

export interface Slice {
  label: string
  value: number
  color: string
}

/** Culorile din scriptul vechi, cuvant cu cuvant. */
export const COLORS = {
  red: 'rgb(255, 99, 132)',
  blue: 'rgb(54, 162, 235)',
  green: 'rgb(87, 255, 60)',
}

function Legend({ items }: { items: Slice[] }) {
  return (
    <div style={{ textAlign: 'center', font: `12px ${FONT}`, color: TEXT, padding: '6px 0' }}>
      {items.map((item) => (
        <span key={item.label} style={{ margin: '0 8px', whiteSpace: 'nowrap' }}>
          <span
            style={{
              display: 'inline-block',
              width: 12,
              height: 12,
              backgroundColor: item.color,
              marginRight: 5,
              verticalAlign: 'middle',
            }}
          />
          {item.label}
        </span>
      ))}
    </div>
  )
}

/**
 * Felii de cerc de la ora 12 in sensul acelor de ceasornic, ca in Chart.js.
 * `inner` peste 0 face inel (`cutoutPercentage`).
 */
function arcs(slices: Slice[], r: number, inner: number, cx: number, cy: number) {
  const total = slices.reduce((sum, s) => sum + s.value, 0)
  if (total <= 0) return []

  let start = -Math.PI / 2

  return slices
    .filter((s) => s.value > 0)
    .map((s) => {
      const sweep = (s.value / total) * Math.PI * 2
      const end = start + sweep
      const large = sweep > Math.PI ? 1 : 0
      const whole = sweep > Math.PI * 2 - 1e-9
      const path = whole
        ? `M ${cx + r} ${cy} A ${r} ${r} 0 1 1 ${cx - r} ${cy} A ${r} ${r} 0 1 1 ${cx + r} ${cy}` +
          (inner > 0
            ? ` M ${cx + inner} ${cy} A ${inner} ${inner} 0 1 0 ${cx - inner} ${cy} A ${inner} ${inner} 0 1 0 ${cx + inner} ${cy}`
            : '')
        : inner > 0
          ? `M ${cx + Math.cos(start) * r} ${cy + Math.sin(start) * r}` +
            ` A ${r} ${r} 0 ${large} 1 ${cx + Math.cos(end) * r} ${cy + Math.sin(end) * r}` +
            ` L ${cx + Math.cos(end) * inner} ${cy + Math.sin(end) * inner}` +
            ` A ${inner} ${inner} 0 ${large} 0 ${cx + Math.cos(start) * inner} ${cy + Math.sin(start) * inner} Z`
          : `M ${cx} ${cy} L ${cx + Math.cos(start) * r} ${cy + Math.sin(start) * r}` +
            ` A ${r} ${r} 0 ${large} 1 ${cx + Math.cos(end) * r} ${cy + Math.sin(end) * r} Z`

      start = end

      return { ...s, path }
    })
}

function Empty({ label }: { label: string }) {
  return (
    <div style={{ font: `12px ${FONT}`, color: TEXT, textAlign: 'center', paddingTop: 40 }}>{label}</div>
  )
}

export function PieChart({ slices, title }: { slices: Slice[]; title: string }) {
  const shapes = arcs(slices, 70, 0, 90, 90)

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Legend items={slices} />
      {shapes.length === 0 ? (
        <Empty label="Nothing to show yet." />
      ) : (
        <svg viewBox="0 0 180 180" style={{ flex: 1, minHeight: 0 }} role="img" aria-label={title}>
          {shapes.map((s) => (
            <path key={s.label} d={s.path} fill={s.color} fillRule="evenodd" />
          ))}
        </svg>
      )}
    </div>
  )
}

/** Inelul de SLA: `cutoutPercentage: 80` si scrisul din mijloc. */
export function DoughnutChart({
  slices,
  centerText,
  centerColor,
}: {
  slices: Slice[]
  centerText: string
  centerColor: string
}) {
  const r = 70
  const shapes = arcs(slices, r, r * 0.8, 90, 90)

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Legend items={slices} />
      {shapes.length === 0 ? (
        <Empty label="Nothing to show yet." />
      ) : (
        <svg viewBox="0 0 180 180" style={{ flex: 1, minHeight: 0 }} role="img" aria-label={centerText}>
          {shapes.map((s) => (
            <path key={s.label} d={s.path} fill={s.color} fillRule="evenodd" />
          ))}
          <text
            x={90}
            y={90}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={centerColor}
            style={{ font: `16px Quicksand, ${FONT}` }}
          >
            {centerText}
          </text>
        </svg>
      )}
    </div>
  )
}

/** Coloanele "Top Ticket Types": o serie, cate o culoare pe bara, de la 0. */
export function BarChart({ bars, label }: { bars: Slice[]; label: string }) {
  const w = 420
  const h = 240
  const pad = { top: 10, right: 10, bottom: 46, left: 34 }
  const max = Math.max(1, ...bars.map((b) => b.value))
  const plotW = w - pad.left - pad.right
  const plotH = h - pad.top - pad.bottom
  const step = bars.length > 0 ? plotW / bars.length : plotW
  const barW = Math.min(48, step * 0.7)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f))

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Legend items={[{ label, value: 0, color: '#a0a0a0' }]} />
      {bars.length === 0 ? (
        <Empty label="No active tickets." />
      ) : (
        <svg viewBox={`0 0 ${w} ${h}`} style={{ flex: 1, minHeight: 0 }} role="img" aria-label={label}>
          {ticks.map((t) => {
            const y = pad.top + plotH - (t / max) * plotH
            return (
              <g key={t}>
                <line x1={pad.left} y1={y} x2={w - pad.right} y2={y} stroke={GRID} />
                <text x={pad.left - 6} y={y + 4} textAnchor="end" fill={TEXT} style={{ font: `11px ${FONT}` }}>
                  {t}
                </text>
              </g>
            )
          })}
          {bars.map((bar, i) => {
            const x = pad.left + i * step + (step - barW) / 2
            const barH = (bar.value / max) * plotH
            return (
              <g key={bar.label}>
                <rect x={x} y={pad.top + plotH - barH} width={barW} height={barH} fill={bar.color || '#57636F'} />
                <text
                  x={x + barW / 2}
                  y={pad.top + plotH - barH - 4}
                  textAnchor="middle"
                  fill={TEXT}
                  style={{ font: `11px ${FONT}` }}
                >
                  {bar.value}
                </text>
                <text
                  x={x + barW / 2}
                  y={pad.top + plotH + 14}
                  textAnchor="middle"
                  fill={TEXT}
                  style={{ font: `11px ${FONT}` }}
                >
                  {bar.label}
                </text>
              </g>
            )
          })}
        </svg>
      )}
    </div>
  )
}

export interface Series {
  label: string
  color: string
  values: number[]
}

/** "Tickets / hours": trei serii pe aceleasi ore, de la 0 in sus. */
export function LineChart({ labels, series }: { labels: string[]; series: Series[] }) {
  const w = 420
  const h = 220
  const pad = { top: 10, right: 12, bottom: 30, left: 34 }
  const max = Math.max(1, ...series.flatMap((s) => s.values))
  const plotW = w - pad.left - pad.right
  const plotH = h - pad.top - pad.bottom
  const n = labels.length
  const x = (i: number) => pad.left + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW)
  const y = (v: number) => pad.top + plotH - (v / max) * plotH
  const ticks = [0, 0.5, 1].map((f) => Math.round(max * f))
  /* Chart.js pune cam zece etichete pe axa, restul le sare. */
  const every = Math.max(1, Math.ceil(n / 10))

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Legend items={series.map((s) => ({ label: s.label, value: 0, color: s.color }))} />
      {n === 0 ? (
        <Empty label="No tickets in this period." />
      ) : (
        <svg viewBox={`0 0 ${w} ${h}`} style={{ flex: 1, minHeight: 0 }} role="img" aria-label="Tickets / hours">
          {ticks.map((t) => (
            <g key={t}>
              <line x1={pad.left} y1={y(t)} x2={w - pad.right} y2={y(t)} stroke={GRID} />
              <text x={pad.left - 6} y={y(t) + 4} textAnchor="end" fill={TEXT} style={{ font: `11px ${FONT}` }}>
                {t}
              </text>
            </g>
          ))}
          {series.map((s) => (
            <polyline
              key={s.label}
              fill="none"
              stroke={s.color}
              strokeWidth={2}
              points={s.values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')}
            />
          ))}
          {labels.map((one, i) =>
            i % every === 0 ? (
              <text
                key={`${one}-${i}`}
                x={x(i)}
                y={h - 10}
                textAnchor="middle"
                fill={TEXT}
                style={{ font: `11px ${FONT}` }}
              >
                {one}
              </text>
            ) : null
          )}
        </svg>
      )}
    </div>
  )
}
